import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import '../styles/adminCoupons.css';

const AdminCoupons = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [coupons, setCoupons] = useState([]);
  const [formData, setFormData] = useState({ name: '', discountType: 'Percentage', discountValue: '', minimumOrderAmount: 0 });
  const [editingId, setEditingId] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchCoupons();
  }, [user, navigate]);

  const fetchCoupons = async () => {
    try {
      setIsFetching(true);
      const res = await apiFetch('/api/coupons');
      const data = await res.json();
      if (res.ok) {
        setCoupons(Array.isArray(data) ? data : []);
      } else {
        showMessage('error', data.message || 'Failed to fetch coupons');
      }
    } catch (error) {
      console.error(error);
      showMessage('error', 'Error loading coupons');
    } finally {
      setIsFetching(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 5000);
  };

  const resetForm = () => {
    setFormData({ name: '', discountType: 'Percentage', discountValue: '', minimumOrderAmount: 0 });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const name = formData.name.trim();
    if (!name) {
      showMessage('error', 'Coupon name is required');
      return;
    }

    const discount = Number(formData.discountValue);
    if (formData.discountValue === '' || isNaN(discount) || discount <= 0) {
      showMessage('error', 'Discount value must be a positive number');
      return;
    }

    if (formData.discountType === 'Percentage' && discount > 100) {
      showMessage('error', 'Discount percentage cannot exceed 100');
      return;
    }

    const minOrder = Number(formData.minimumOrderAmount);
    if (isNaN(minOrder) || minOrder < 0) {
      showMessage('error', 'Minimum order amount must be a non-negative number');
      return;
    }

    // Check client-side duplicate name
    const duplicate = coupons.find(
      (c) => (c.name || c.code || '').toLowerCase() === name.toLowerCase() && c._id !== editingId
    );
    if (duplicate) {
      showMessage('error', 'Coupon name already exists');
      return;
    }

    setIsSubmitting(true);
    try {
      const url = editingId ? `/api/coupons/${editingId}` : '/api/coupons';
      const method = editingId ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method,
        body: JSON.stringify({
          code: name,
          name,
          discountType: formData.discountType,
          discountValue: discount,
          minimumOrderAmount: minOrder,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        showMessage('error', data.message || 'Failed to save coupon');
        return;
      }

      showMessage(
        'success',
        editingId ? 'Coupon updated successfully' : 'Coupon created successfully'
      );
      resetForm();
      fetchCoupons();
    } catch (error) {
      console.error(error);
      showMessage('error', 'An error occurred while saving coupon');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (coupon) => {
    setEditingId(coupon._id);
    setFormData({
      name: coupon.name || coupon.code || '',
      discountType: coupon.discountType || 'Percentage',
      discountValue: coupon.discountValue !== undefined ? coupon.discountValue : (coupon.discountPercentage || ''),
      minimumOrderAmount: coupon.minimumOrderAmount !== undefined ? coupon.minimumOrderAmount : 0,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancel = () => {
    resetForm();
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this coupon?');
    if (!confirmed) return;

    try {
      const res = await apiFetch(`/api/coupons/${id}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) {
        showMessage('error', data.message || 'Failed to delete coupon');
        return;
      }
      showMessage('success', 'Coupon deleted successfully');
      setCoupons(coupons.filter((c) => c._id !== id));
    } catch (error) {
      console.error(error);
      showMessage('error', 'Delete request failed');
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    const newStatus = !currentStatus;
    try {
      const res = await apiFetch(`/api/coupons/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ isActive: newStatus }),
      });
      const data = await res.json();
      if (!res.ok) {
        showMessage('error', data.message || 'Failed to update coupon status');
        return;
      }
      setCoupons(coupons.map((c) => (c._id === id ? { ...c, isActive: newStatus } : c)));
      showMessage('success', `Coupon ${newStatus ? 'enabled' : 'disabled'} successfully`);
    } catch (error) {
      console.error(error);
      showMessage('error', 'Error updating coupon status');
    }
  };

  return (
    <div className="admin-coupons-container">
      <div className="admin-coupons-header">
        <div>
          <h2 style={{ color: '#f97316', marginBottom: '10px' }}>Coupons Management</h2>
          <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
            Create and manage promotional discount coupons for your store.
          </p>
        </div>
        <button className="btn" onClick={() => navigate('/admin')} style={{ height: '45px', background: 'var(--btn-secondary-bg, #3f3f46)', color: 'var(--color-text-primary)' }}>
          Back to Dashboard
        </button>
      </div>

      {/* Top Form Card */}
      <div className="admin-coupons-card">
        <h3 style={{ color: '#f97316', marginBottom: '20px' }}>
          {editingId ? 'Edit Coupon' : 'Create Coupon'}
        </h3>

        {message && (
          <div
            style={{
              background: message.type === 'error' ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
              color: message.type === 'error' ? '#fecaca' : '#bef264',
              border: `1px solid ${message.type === 'error' ? 'rgba(248,113,113,0.3)' : 'rgba(134,239,172,0.3)'}`,
              borderRadius: '10px',
              padding: '12px 16px',
              marginBottom: '20px',
              fontSize: '0.95rem',
            }}
          >
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'grid', gap: '20px' }}>
          <div>
            <label className="admin-coupons-label" htmlFor="coupon-name">
              Coupon Name <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              id="coupon-name"
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. SUMMER50"
              className="admin-coupons-input"
            />
          </div>

          <div>
            <label className="admin-coupons-label" htmlFor="discount-type">
              Discount Type <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <select
              id="discount-type"
              value={formData.discountType}
              onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
              className="admin-coupons-input"
            >
              <option value="Percentage">Percentage (%)</option>
              <option value="FixedAmount">Fixed Amount (₹)</option>
            </select>
          </div>

          <div>
            <label className="admin-coupons-label" htmlFor="discount-value">
              Discount Value {formData.discountType === 'Percentage' ? '(%)' : '(₹)'} <span style={{ color: '#ef4444' }}>*</span>
            </label>
            <input
              id="discount-value"
              type="number"
              required
              min="1"
              max={formData.discountType === 'Percentage' ? "100" : undefined}
              value={formData.discountValue}
              onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
              placeholder={formData.discountType === 'Percentage' ? "Enter percentage (1 - 100)" : "Enter amount (e.g. 200)"}
              className="admin-coupons-input"
            />
          </div>

          <div>
            <label className="admin-coupons-label" htmlFor="minimum-order-amount">
              Minimum Order Amount (₹)
            </label>
            <input
              id="minimum-order-amount"
              type="number"
              min="0"
              value={formData.minimumOrderAmount}
              onChange={(e) => setFormData({ ...formData, minimumOrderAmount: e.target.value })}
              className="admin-coupons-input"
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
            <button type="submit" className="btn" disabled={isSubmitting} style={{ minWidth: '160px' }}>
              {isSubmitting
                ? editingId ? 'Updating...' : 'Creating...'
                : editingId ? 'Update Coupon' : 'Create Coupon'}
            </button>
            {editingId && (
              <button type="button" onClick={handleCancel} className="admin-coupons-cancel-btn">
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      {/* Coupons Table */}
      <div style={{ marginTop: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3 style={{ color: '#f97316', margin: 0 }}>All Coupons</h3>
          <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.95rem' }}>
            Total: {coupons.length}
          </span>
        </div>

        {isFetching ? (
          <div style={{ padding: '30px', background: 'var(--color-surface-1)', borderRadius: '12px', color: '#f97316', textAlign: 'center', border: '1px solid var(--color-border-light, rgba(255,255,255,0.05))' }}>
            Loading coupons...
          </div>
        ) : coupons.length === 0 ? (
          <div style={{ padding: '30px', background: 'var(--color-surface-1)', borderRadius: '12px', color: 'var(--color-text-secondary)', textAlign: 'center', border: '1px solid var(--color-border-light, rgba(255,255,255,0.05))' }}>
            No coupons found. Create your first coupon above.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', background: 'var(--color-surface-1)', borderRadius: '12px', border: '1px solid var(--color-border-light, rgba(255,255,255,0.05))' }}>
            <table className="admin-coupons-table">
              <thead>
                <tr className="admin-coupons-row">
                  <th className="admin-coupons-th">Coupon Name</th>
                  <th className="admin-coupons-th">Discount Type</th>
                  <th className="admin-coupons-th">Discount Value</th>
                  <th className="admin-coupons-th">Min. Order</th>
                  <th className="admin-coupons-th">Times Used</th>
                  <th className="admin-coupons-th">Status</th>
                  <th className="admin-coupons-th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon._id} className="admin-coupons-row">
                    <td className="admin-coupons-td" style={{ fontWeight: '600', color: 'var(--color-text-primary)' }}>
                      {coupon.code}
                    </td>
                    <td className="admin-coupons-td">
                      <span className="admin-coupons-type-badge">
                        {coupon.discountType || 'Percentage'}
                      </span>
                    </td>
                    <td className="admin-coupons-td">
                      <span className="admin-coupons-discount-badge">
                        {coupon.discountType === 'FixedAmount'
                          ? `₹${coupon.discountValue}`
                          : `${coupon.discountValue !== undefined ? coupon.discountValue : coupon.discountPercentage}% OFF`}
                      </span>
                    </td>
                    <td className="admin-coupons-td">
                      {coupon.minimumOrderAmount > 0
                        ? <span className="admin-coupons-used-count-badge">₹{Number(coupon.minimumOrderAmount).toLocaleString('en-IN')}</span>
                        : <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>None</span>}
                    </td>
                    <td className="admin-coupons-td">
                      <span className="admin-coupons-used-count-badge">
                        {coupon.usedCount || 0}
                      </span>
                    </td>
                    <td className="admin-coupons-td">
                      <span className={coupon.isActive ? 'admin-coupons-active-badge' : 'admin-coupons-inactive-badge'}>
                        {coupon.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="admin-coupons-td">
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <button
                          onClick={() => handleEdit(coupon)}
                          className="admin-coupons-edit-btn"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleStatus(coupon._id, coupon.isActive)}
                          className={coupon.isActive ? 'admin-coupons-disable-btn' : 'admin-coupons-enable-btn'}
                        >
                          {coupon.isActive ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => handleDelete(coupon._id)}
                          className="admin-coupons-delete-btn"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCoupons;
